"use client";

import { useParams } from "next/navigation";
import Link from "next/link";
import { useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { useState } from "react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageTitle } from "@/lib/usePageTitle";
import {
  ServerIcon,
  GlobeIcon,
  CopyIcon,
  CheckIcon,
  ArrowRightIcon,
  TagIcon,
} from "@/components/icons";

export default function ServerDetailPage() {
  const params = useParams();
  const slug = params.slug as string;

  const listing = useQuery(api.serverListings.getBySlug, { slug });

  const [copied, setCopied] = useState(false);

  usePageTitle(listing ? listing.name : "Server Details");

  const serverAddress = listing?.serverIp
    ? listing.serverPort ? `${listing.serverIp}:${listing.serverPort}` : listing.serverIp
    : null;

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (listing === undefined) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Navbar />
        <main className="flex-1 py-12">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 space-y-4">
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-48" />
            <Skeleton className="h-32" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (listing === null) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Navbar />
        <main className="flex-1 py-20 text-center">
          <GlobeIcon className="mx-auto h-12 w-12 text-muted" />
          <h1 className="mt-4 text-2xl font-bold text-foreground">Server not found</h1>
          <p className="mt-2 text-muted">This listing may have been removed or the URL is incorrect.</p>
          <Link href="/servers" className="mt-6 inline-block">
            <Button variant="outline">← Back to Servers</Button>
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const isUnclaimed = listing.status === "active" || listing.status === "pending_claim";

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />
      <main className="flex-1">
        {/* Hero */}
        <section className="border-b border-border bg-foreground/5 py-10">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <Link href="/servers" className="text-sm text-muted hover:text-foreground transition-colors mb-4 inline-block">
              ← Back to Servers
            </Link>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-3xl font-bold text-foreground">{listing.name}</h1>
                  {listing.isFeatured && (
                    <Badge className="bg-primary text-primary-foreground">Featured</Badge>
                  )}
                  {listing.lastStatusCheckedAt && (
                    <Badge
                      variant={listing.isOnline ? "default" : "secondary"}
                      className={listing.isOnline ? "bg-emerald-600 text-white" : ""}
                    >
                      <span className={`mr-1.5 inline-block h-2 w-2 rounded-full ${listing.isOnline ? "bg-white" : "bg-muted-foreground/50"}`} />
                      {listing.isOnline ? "Online" : "Offline"}
                    </Badge>
                  )}
                </div>
                {listing.description && (
                  <p className="mt-2 text-muted max-w-xl">{listing.description}</p>
                )}
              </div>
              {isUnclaimed && (
                <Link href={`/servers/${slug}/claim`}>
                  <Button>
                    Claim This Server
                    <ArrowRightIcon className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              )}
            </div>

            {/* Tags */}
            {listing.tags && listing.tags.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {listing.tags.map((tag: string) => (
                  <Badge key={tag} variant="secondary">
                    <TagIcon className="mr-1 h-3 w-3" />
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Content */}
        <section className="py-8">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 space-y-6">
            {/* Server info card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <ServerIcon className="h-4 w-4" />
                  Server Info
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {serverAddress && (
                  <div className="flex items-center justify-between rounded-lg border border-border bg-background p-3">
                    <div className="flex items-center gap-2">
                      <ServerIcon className="h-4 w-4 text-muted" />
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-muted">Address</p>
                        <code className="font-medium">{serverAddress}</code>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => copyToClipboard(serverAddress)}
                      aria-label="Copy server address"
                    >
                      {copied ? (
                        <CheckIcon className="h-4 w-4 text-primary" />
                      ) : (
                        <CopyIcon className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                )}

                {listing.gameMode && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted">Game Mode</span>
                    <Badge variant="outline">{listing.gameMode}</Badge>
                  </div>
                )}

                {listing.isOnline && (typeof listing.playerCount === "number" || typeof listing.maxPlayers === "number") && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted">Players Online</span>
                    <span className="font-medium">{listing.playerCount ?? 0} / {listing.maxPlayers ?? "?"}</span>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-muted">Status</span>
                  <Badge variant={listing.status === "claimed" ? "default" : "secondary"}>
                    {listing.status === "claimed" ? "Claimed" : listing.status === "pending_claim" ? "Pending Claim" : "Unclaimed"}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Claim CTA for unclaimed listings */}
            {isUnclaimed && (
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="py-6 flex items-start gap-4">
                  <GlobeIcon className="h-8 w-8 text-primary mt-0.5 shrink-0" />
                  <div>
                    <h3 className="font-semibold text-foreground">Is this your server?</h3>
                    <p className="mt-1 text-sm text-muted">
                      Claim your listing to manage your server profile, connect with players, and get featured placement.
                    </p>
                    <div className="mt-3 flex gap-2 flex-wrap">
                      <Link href={`/servers/${slug}/claim`}>
                        <Button size="sm">
                          Claim this server
                          <ArrowRightIcon className="ml-1.5 h-3.5 w-3.5" />
                        </Button>
                      </Link>
                      <Link href="/signup">
                        <Button variant="outline" size="sm">Create Account</Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Back button */}
            <div className="pt-2">
              <Link href="/servers">
                <Button variant="ghost" size="sm">← Back to all servers</Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
